import { useState, useEffect } from "react";
import { getUserProfile, updateUserProfile, updateUserDetails, GetUserDetails, getCountries, getStates, getTimezones, getLanguages, normalizeTimezone } from "../service/rpc";
import { getSession } from "../service/auth";
import toast from "react-hot-toast";
import "../styles/Profile.css";

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const defaultFormData = {
    name: "",
    email: "",
    street: "",
    street2: "",
    city: "",
    zip: "",
    country_id: "",
    state_id: "",
    lang: "en_US",
    tz: normalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [initialData, setInitialData] = useState(defaultFormData);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    fetchProfile();
    loadCountries();
    loadTimezones();
    loadLanguages();
  }, []);


  const isChanged = JSON.stringify(formData) !== JSON.stringify(initialData);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const session = getSession();
      if (!session || !session.uid) return;

      // Fetch both partner info and user details
      const [partnerData, userData] = await Promise.all([
        getUserProfile(session.uid),
        GetUserDetails(session.uid)
      ]);

      if (partnerData) {
        setProfile(partnerData);
        const user = userData && userData.length > 0 ? userData[0] : {};

        const data = {
          name: partnerData.name || "",
          email: partnerData.email || "",
          street: partnerData.street || "",
          street2: partnerData.street2 || "",
          city: partnerData.city || "",
          zip: partnerData.zip || "",
          country_id: partnerData.country_id ? partnerData.country_id[0] : "",
          state_id: partnerData.state_id ? partnerData.state_id[0] : "",
          lang: user.lang || "en_US",
          tz: normalizeTimezone(user.tz || Intl.DateTimeFormat().resolvedOptions().timeZone),
          profile_type: partnerData.profile_type || user.profile_type || ""
        };

        setFormData(data);
        setInitialData(data);

        if (partnerData.country_id) loadStates(partnerData.country_id[0]);
      }
    } catch (err) {
      console.error("Failed to fetch profile", err);
      setError(err.message || "Failed to load profile details.");
    } finally {
      setLoading(false);
    }
  };
  const loadCountries = async () => {
    try { setCountries(await getCountries()); }
    catch (err) { console.error("Countries load failed", err); }
  };

  const loadStates = async (countryId) => {
    try { setStates(await getStates(countryId)); }
    catch (err) { console.error("States load failed", err); }
  };

  const loadTimezones = async () => {
    try { setTimezones(await getTimezones()); }
    catch (err) { console.error("Timezones load failed", err); }
  };

  const loadLanguages = async () => {
    try { setLanguages(await getLanguages()); }
    catch (err) { console.error("Languages load failed", err); }
  };

  const handleCountryChange = (e) => {
    const cid = e.target.value;
    setFormData({ ...formData, country_id: cid, state_id: "" });
    loadStates(cid || null);
  };

  const handleStateChange = (e) => {
    const sid = e.target.value;
    const sel = states.find(s => s.id === parseInt(sid));
    if (sel?.country_id) {
      setFormData({ ...formData, state_id: sid, country_id: sel.country_id[0] });
    } else {
      setFormData({ ...formData, state_id: sid });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const session = getSession();
      await Promise.all([
        updateUserProfile(profile.id, {
          name: formData.name,
          email: formData.email,
          street: formData.street,
          street2: formData.street2,
          city: formData.city,
          zip: formData.zip,
          country_id: formData.country_id ? parseInt(formData.country_id) : false,
          state_id: formData.state_id ? parseInt(formData.state_id) : false
        }),
        updateUserDetails(session.uid, {
          lang: formData.lang,
          tz: normalizeTimezone(formData.tz)
        })
      ]);
      toast.success("Profile updated successfully!");
      fetchProfile();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="pf-wrap">
        <div className="pf-error" style={{ border: '1px solid #fee2e2', background: '#fff', padding: '40px', borderRadius: '12px' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>⚠️</div>
          <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '18px' }}>Error Loading Profile</p>
          <p style={{ fontSize: '14px', marginTop: '8px', color: '#64748b', maxWidth: '400px', margin: '8px auto' }}>{error}</p>
          <button 
            className="pf-btn" 
            style={{ marginTop: '24px', background: '#6b7fc4', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }} 
            onClick={fetchProfile}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!profile && !loading) {
    return <div className="pf-error">Unable to load profile. Please sign in again.</div>;
  }

  return (
    <div className="pf-wrap">
      <div className="page-header">
        <h1 className="page-header__title">Profile</h1>
      </div>
      {/* Unified form card */}
      <div className={`pf-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading profile...</p>
          </div>
        )}
        {/* Action row — now at top right, only when changed */}
        {isChanged && (
          <div className="pf-actions">
            <button className="pf-btn pf-btn--icon pf-btn--cancel" onClick={() => { fetchProfile(); }} title="Discard">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <button className="pf-btn pf-btn--icon pf-btn--save" onClick={handleSave} disabled={saving} title={saving ? "Saving…" : "Save Changes"}>
              {saving ? (
                <div className="pf-spinner pf-spinner--small"></div>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          </div>
        )}

        <div className="pf-grid">
          {/* Row 1: Name + Email */}
          <div className="pf-field">
            <label>Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
            />
          </div>
          <div className="pf-field">
            <label>Email Address</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@example.com"
            />
          </div>

          {/* Row 2: Street */}
          <div className="pf-field pf-field--full">
            <label>Street</label>
            <input
              type="text"
              value={formData.street}
              onChange={e => setFormData({ ...formData, street: e.target.value })}
              placeholder="Street address"
            />
          </div>

          {/* Row 3: Street 2 */}
          <div className="pf-field pf-field--full">
            <label>Street 2</label>
            <input
              type="text"
              value={formData.street2}
              onChange={e => setFormData({ ...formData, street2: e.target.value })}
              placeholder="Apt, suite, etc."
            />
          </div>

          {/* Row 4: City + Zip */}
          <div className="pf-field">
            <label>City</label>
            <input
              type="text"
              value={formData.city}
              onChange={e => setFormData({ ...formData, city: e.target.value })}
              placeholder="City"
            />
          </div>
          <div className="pf-field">
            <label>Zip Code</label>
            <input
              type="text"
              value={formData.zip}
              onChange={e => setFormData({ ...formData, zip: e.target.value })}
              placeholder="Zip"
            />
          </div>

          {/* Row 5: Country + State */}
          <div className="pf-field">
            <label>Country</label>
            <select value={formData.country_id} onChange={handleCountryChange}>
              <option value="">Select Country</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="pf-field">
            <label>State</label>
            <select value={formData.state_id} onChange={handleStateChange}>
              <option value="">Select State</option>
              {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {/* Row 6: Language + Timezone */}
          <div className="pf-field">
            <label>Language</label>
            <select
              value={formData.lang}
              onChange={e => setFormData({ ...formData, lang: e.target.value })}
            >
              <option value="">Select Language</option>
              {languages.map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
          <div className="pf-field">
            <label>Timezone</label>
            <select
              value={formData.tz}
              onChange={e => setFormData({ ...formData, tz: e.target.value })}
            >
              <option value="">Select Timezone</option>
              {timezones.map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
        </div>

      </div>
    </div>
  );
}
